'use client';
import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

type Props = {
  // Provide either a loader function that returns a dynamic import,
  // or a modulePath string (not recommended) for static imports.
  loader?: () => Promise<any>;
  modulePath?: string;
  exportName?: string;
  placeholder?: React.ReactNode;
  rootMargin?: string;
};

export default function LazyLoadWrapper({ loader, modulePath, exportName, placeholder = null, rootMargin = '200px' }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [DynamicComp, setDynamicComp] = useState<React.ComponentType<any> | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (visible) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { root: null, rootMargin }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, rootMargin, visible]);

  useEffect(() => {
    if (!visible) return;

    // Map of known module paths to static import functions to avoid dynamic import expressions.
    const importMap: Record<string, () => Promise<any>> = {
      '@/letsgo/templates/ROICalculator': () => import('@/letsgo/templates/ROICalculator'),
      '@/letsgo/templates/UserCount': () => import('@/letsgo/templates/UserCount'),
      '@/letsgo/templates/PlatformSelector': () => import('@/letsgo/templates/PlatformSelector'),
      // add other known templates here if needed
    };

    const resolver = async () => {
      if (typeof loader === 'function') {
        return loader();
      }
      if (modulePath && importMap[modulePath]) {
        return importMap[modulePath]();
      }
      // If we don't recognize the modulePath, fail fast so bundler warnings are avoided.
      throw new Error(`LazyLoadWrapper: unknown modulePath "${modulePath}"`);
    };

    const dyn = dynamic(
      () =>
        resolver().then((m: any) =>
          exportName ? m[exportName] || m.default || m : m.default || m
        ),
      { ssr: false, loading: () => (placeholder ? placeholder : null) }
    );
    setDynamicComp(() => dyn as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, modulePath, exportName, placeholder]);

  return (
    <div ref={ref}>
      {DynamicComp ? <DynamicComp /> : placeholder}
    </div>
  );
}

